import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch } from 'react-icons/md';
import './MasterData.css';

interface Item {
  itemId: number;
  itemCode: string;
  itemName: string;
  itemType: string;
  category: string;
  uom: string;
  standardCost: number;
  sellingPrice: number;
  reorderLevel: number;
  isActive: string;
}

const Items: React.FC = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [filterType, setFilterType] = useState('ALL');

  useEffect(() => {
    // Mock data
    setItems([
      {
        itemId: 1,
        itemCode: 'RM-WOOD-001',
        itemName: 'Mahogany Wood - Grade A',
        itemType: 'RAW_MATERIAL',
        category: 'Wood',
        uom: 'BD FT',
        standardCost: 450.00,
        sellingPrice: 0,
        reorderLevel: 500,
        isActive: 'Y'
      },
      {
        itemId: 2,
        itemCode: 'RM-FABRIC-001',
        itemName: 'Leather Fabric - Black',
        itemType: 'RAW_MATERIAL',
        category: 'Fabric',
        uom: 'YARD',
        standardCost: 850.00,
        sellingPrice: 0,
        reorderLevel: 200,
        isActive: 'Y'
      },
      {
        itemId: 3,
        itemCode: 'FG-SOFA-001',
        itemName: '3-Seater Sofa - Modern Design',
        itemType: 'FINISHED_GOOD',
        category: 'Sofa',
        uom: 'PCS',
        standardCost: 15000.00,
        sellingPrice: 25000.00,
        reorderLevel: 10,
        isActive: 'Y'
      },
      {
        itemId: 4,
        itemCode: 'FG-TABLE-001',
        itemName: 'Dining Table - 6 Seater',
        itemType: 'FINISHED_GOOD',
        category: 'Table',
        uom: 'PCS',
        standardCost: 12000.00,
        sellingPrice: 20000.00,
        reorderLevel: 5,
        isActive: 'Y'
      },
      {
        itemId: 5,
        itemCode: 'FG-CHAIR-001',
        itemName: 'Office Chair - Executive',
        itemType: 'FINISHED_GOOD',
        category: 'Chair',
        uom: 'PCS',
        standardCost: 3500.00,
        sellingPrice: 6000.00,
        reorderLevel: 20,
        isActive: 'Y'
      }
    ]);
  }, []);

  const filteredItems = items.filter(item => {
    const matchesSearch =
      item.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.itemCode.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'ALL' || item.itemType === filterType;
    return matchesSearch && matchesType;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Items / Products</h2>
            <p className="subtitle">Manage raw materials and finished goods</p>
          </div>
          <button className="btn btn-primary" onClick={() => { setEditingItem(null); setShowModal(true); }}>
            <MdAdd /> Add New Item
          </button>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="filter-tabs">
              {['ALL', 'RAW_MATERIAL', 'FINISHED_GOOD'].map(type => (
                <button
                  key={type}
                  className={`filter-tab ${filterType === type ? 'active' : ''}`}
                  onClick={() => setFilterType(type)}
                >
                  {type.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Item Name</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th>UOM</th>
                  <th>Standard Cost</th>
                  <th>Selling Price</th>
                  <th>Reorder Level</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map(item => (
                  <tr key={item.itemId}>
                    <td className="code">{item.itemCode}</td>
                    <td className="name">{item.itemName}</td>
                    <td>
                      <span className={`badge ${item.itemType === 'RAW_MATERIAL' ? 'badge-orange' : 'badge-green'}`}>
                        {item.itemType.replace('_', ' ')}
                      </span>
                    </td>
                    <td>{item.category}</td>
                    <td>{item.uom}</td>
                    <td>{formatCurrency(item.standardCost)}</td>
                    <td>{item.sellingPrice > 0 ? formatCurrency(item.sellingPrice) : '-'}</td>
                    <td>{item.reorderLevel}</td>
                    <td>
                      <span className={`badge ${item.isActive === 'Y' ? 'badge-success' : 'badge-danger'}`}>
                        {item.isActive === 'Y' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon btn-edit" onClick={() => { setEditingItem(item); setShowModal(true); }}>
                          <MdEdit />
                        </button>
                        <button className="btn-icon btn-delete" onClick={() => {
                          if (window.confirm('Delete this item?')) {
                            setItems(items.filter(i => i.itemId !== item.itemId));
                          }
                        }}>
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <ItemModal
          item={editingItem}
          onClose={() => setShowModal(false)}
          onSave={(item) => {
            if (editingItem) {
              setItems(items.map(i => i.itemId === item.itemId ? item : i));
            } else {
              setItems([...items, { ...item, itemId: Date.now() }]);
            }
            setShowModal(false);
          }}
        />
      )}
    </MainLayout>
  );
};

interface ItemModalProps {
  item: Item | null;
  onClose: () => void;
  onSave: (item: Item) => void;
}

const ItemModal: React.FC<ItemModalProps> = ({ item, onClose, onSave }) => {
  const [formData, setFormData] = useState<Item>(
    item || {
      itemId: 0,
      itemCode: '',
      itemName: '',
      itemType: 'RAW_MATERIAL',
      category: '',
      uom: 'PCS',
      standardCost: 0,
      sellingPrice: 0,
      reorderLevel: 0,
      isActive: 'Y'
    }
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{item ? 'Edit Item' : 'Add New Item'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Item Code *</label>
                <input type="text" required value={formData.itemCode}
                  onChange={(e) => setFormData({ ...formData, itemCode: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Item Name *</label>
                <input type="text" required value={formData.itemName}
                  onChange={(e) => setFormData({ ...formData, itemName: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Item Type *</label>
                <select value={formData.itemType}
                  onChange={(e) => setFormData({ ...formData, itemType: e.target.value })}>
                  <option value="RAW_MATERIAL">Raw Material</option>
                  <option value="FINISHED_GOOD">Finished Good</option>
                </select>
              </div>
              <div className="form-group">
                <label>Category</label>
                <input type="text" value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Unit of Measure *</label>
                <select value={formData.uom}
                  onChange={(e) => setFormData({ ...formData, uom: e.target.value })}>
                  <option value="PCS">PCS</option>
                  <option value="KG">KG</option>
                  <option value="M">M</option>
                  <option value="YARD">YARD</option>
                  <option value="BD FT">BD FT</option>
                  <option value="SET">SET</option>
                </select>
              </div>
              <div className="form-group">
                <label>Reorder Level</label>
                <input type="number" value={formData.reorderLevel}
                  onChange={(e) => setFormData({ ...formData, reorderLevel: parseInt(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Standard Cost</label>
                <input type="number" step="0.01" value={formData.standardCost}
                  onChange={(e) => setFormData({ ...formData, standardCost: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="form-group">
                <label>Selling Price</label>
                <input type="number" step="0.01" value={formData.sellingPrice}
                  onChange={(e) => setFormData({ ...formData, sellingPrice: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Status</label>
                <select value={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.value })}>
                  <option value="Y">Active</option>
                  <option value="N">Inactive</option>
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">{item ? 'Update' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Items;
